
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData } from '../../types';
import { SocialIcon } from '../../utils/icons';
import { getInitials, formatLeadIndustry, formatRevenue } from '../../utils/helpers';
import { Card, CardHeader, DetailItem, Tag, SectionLabel } from '../common/Atomic';
import { getStatusConfig, getTransitionalStatusLabel, getLeadSubstatusDisplay } from '../../utils/statusMapping';
import { getEmailScoreConfig } from '../../utils/emailScore';
import { EmailIntelBadge } from '../atoms/ChannelIntel';
import { ChannelVerifiedBadge } from '../atoms/VerifiedBadge';

interface OverviewTabProps {
  data: MappedLeadData;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data }) => {
  const [companyLogoError, setCompanyLogoError] = useState(false);
  // Helper to validate and format URLs
  const formatURL = (url: string | null): string | null => {
    if (!url || url === '#' || url === 'Not available' || url === 'Unknown') return null;
    return url.startsWith('http') ? url : `https://${url}`;
  };

  // Helper to format social media URLs properly (handles both full URLs and @handles)
  const formatSocialURL = (value: string | null, platform: string, isCompany: boolean = false): string | null => {
    if (!value || value === '#' || value === 'Not available' || value === 'Unknown') return null;

    // If already a full URL, return it
    if (value.startsWith('http')) return value;

    // If it's a handle (starts with @), convert to proper URL based on platform
    if (value.startsWith('@')) {
      const handle = value.substring(1); // Remove @ symbol
      switch (platform.toLowerCase()) {
        case 'twitter':
        case 'x':
          return `https://twitter.com/${handle}`;
        case 'instagram':
          return `https://instagram.com/${handle}`;
        case 'facebook':
          return `https://facebook.com/${handle}`;
        case 'youtube':
          return `https://youtube.com/@${handle}`;
        case 'linkedin':
          // Use /company/ for company profiles, /in/ for personal profiles
          return isCompany ? `https://linkedin.com/company/${handle}` : `https://linkedin.com/in/${handle}`;
        default:
          return `https://${value}`;
      }
    }

    // Otherwise, assume it's a domain or path and prepend https://
    return `https://${value}`;
  };

  // Use centralized status config from utils/statusMapping.ts
  const statusConfig = getStatusConfig(data.status);
  const transitionalLabel = getTransitionalStatusLabel(data.status);
  const substatusLabel = getLeadSubstatusDisplay(data.status, data.salesSubstatus);
  const statusBadge = {
    // Transitional substatus ("Writing message…" — under ENRICHED now) wins;
    // otherwise the DB substatus when the lead carries one; else the umbrella.
    label: transitionalLabel ?? (substatusLabel ?? statusConfig.label),
    parentLabel: transitionalLabel || substatusLabel ? statusConfig.label : null,
    color: `${statusConfig.tailwindColor} ${statusConfig.tailwindBorder} ${statusConfig.tailwindBg}`,
  };
  const personSocials = [
    { p: 'email', v: data.email, l: 'Email', emailScore: data.email_score },
    { p: 'phone', v: data.phone, l: 'Phone' },
    { p: 'mobile', v: data.mobile, l: 'Mobile' },
    { p: 'linkedin', v: data.leadLinkedin, l: 'LinkedIn' },
    { p: 'twitter', v: data.leadTwitter, l: 'Twitter' },
    { p: 'facebook', v: data.leadFacebook, l: 'Facebook' },
    { p: 'instagram', v: data.leadInstagram, l: 'Instagram' },
    { p: 'youtube', v: data.leadYoutube, l: 'YouTube' },
    { p: 'tiktok', v: data.leadTiktok, l: 'TikTok' },
    { p: 'discord', v: data.leadDiscord, l: 'Discord' },
    { p: 'line', v: data.leadLine, l: 'Line' },
    { p: 'wechat', v: data.leadWechat, l: 'WeChat' },
    { p: 'whatsapp', v: data.leadWhatsapp, l: 'WhatsApp' },
    { p: 'messenger', v: data.leadMessenger, l: 'Messenger' },
    { p: 'google_places', v: data.leadGooglePlaces, l: 'Google' },
    { p: 'tripadvisor', v: data.leadTripadvisor, l: 'TripAdvisor' },
  ].filter(s => s.v && s.v !== '#' && s.v !== 'Not available' && s.v !== 'Unknown');

  const companySocials = [
    { p: 'email', v: data.companyEmail, l: 'Email' },
    { p: 'phone', v: data.companyPhone, l: 'Phone' },
    { p: 'website', v: data.companyWebsite, l: 'Website' },
    { p: 'linkedin', v: data.companyLinkedin, l: 'LinkedIn' },
    { p: 'twitter', v: data.companyTwitter, l: 'Twitter' },
    { p: 'facebook', v: data.companyFacebook, l: 'Facebook' },
    { p: 'instagram', v: data.companyInstagram, l: 'Instagram' },
    { p: 'youtube', v: data.companyYoutube, l: 'YouTube' },
    { p: 'tiktok', v: data.companyTiktok, l: 'TikTok' },
    { p: 'discord', v: data.companyDiscord, l: 'Discord' },
    { p: 'line', v: data.companyLine, l: 'Line' },
    { p: 'wechat', v: data.companyWechat, l: 'WeChat' },
    { p: 'whatsapp', v: data.companyWhatsapp, l: 'WhatsApp' },
    { p: 'messenger', v: data.companyMessenger, l: 'Messenger' },
    { p: 'google_places', v: data.companyGooglePlaces, l: 'Google' },
    { p: 'tripadvisor', v: data.companyTripadvisor, l: 'TripAdvisor' },
  ].filter(s => s.v && s.v !== '#' && s.v !== 'Not available' && s.v !== 'Unknown');

  // Validate URLs for header buttons
  const websiteURL = formatURL(data.companyWebsite);

  const keyMetrics = [
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>, label: 'Employees', value: (data.size_range || data.companyEmployees) && (data.size_range || data.companyEmployees) !== 'Unknown' ? (data.size_range || data.companyEmployees) : 'UNKNOWN', color: 'text-[#5260FF]' },
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>, label: 'Company Stage', value: data.companyStage && data.companyStage !== 'Unknown' ? data.companyStage : 'UNKNOWN', color: 'text-[#15FBFF]' },
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>, label: 'Revenue', value: formatRevenue(data.revenue || data.revenueRange), color: 'text-[#4FCDCF]' }
  ];

  const contacts = [
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>, label: 'Email', value: data.email || data.companyEmail, color: 'text-gray-400', isLink: false },
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>, label: 'Phone', value: data.phone || data.companyPhone, color: 'text-gray-400', isLink: false },
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>, label: 'Mobile', value: data.mobile, color: 'text-gray-400', isLink: false },
      { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>, label: 'Website', value: data.companyWebsite, color: 'text-[#15FBFF]', isLink: true, href: websiteURL }
  ].filter(c => {
    // Keep item if it has a value AND (it's not a link OR it has a valid href)
    return c.value && c.value !== 'Not available' && c.value !== 'Unknown' && (!c.isLink || c.href);
  });

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Header Section - Compact Layout */}
      <div className="relative overflow-hidden rounded-[24px] border border-white/8 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.10),transparent_24%),radial-gradient(circle_at_bottom_left,rgba(82,96,255,0.10),transparent_26%),linear-gradient(180deg,rgba(18,22,30,0.96),rgba(8,10,15,0.98))] p-4 sm:p-5 shadow-[0_18px_36px_rgba(0,0,0,0.22)]">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.015))] pointer-events-none"></div>
        <div className="relative z-10 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_96px] xl:items-start">
            {/* Left: Lead Info */}
            <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-black uppercase tracking-[0.18em] text-[#95A0C1]">{data.companyName}</span>
                    {statusBadge.parentLabel && (
                        <Tag label={statusBadge.parentLabel} color={`${statusBadge.color} uppercase tracking-[0.18em] text-[9px] px-2 py-0.5 rounded-full opacity-50`} />
                    )}
                    <Tag label={statusBadge.label} color={`${statusBadge.color} uppercase tracking-[0.18em] text-[10px] px-2.5 py-1 rounded-full`} />
                </div>

                <h1 className="text-[24px] font-black leading-[1.05] tracking-[-0.03em] text-white sm:text-[28px]">{data.leadName}</h1>

                <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#CDD4EA]">{data.leadRole}</p>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#7E88A8]">Industry</div>
                        <div className="mt-1.5 text-sm font-semibold text-white">{data.companyIndustry || 'Unknown'}</div>
                    </div>
                    <div className="rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#7E88A8]">Location</div>
                        <div className="mt-1.5 text-sm font-semibold text-white">{data.companyCountry || 'Unknown'}</div>
                        {data.cnae_code && data.cnae_text && (
                          <div className="mt-1 text-xs text-[#8A94B2]">
                            {String(data.cnae_text).split(',')[0].trim()}
                          </div>
                        )}
                    </div>
                </div>

                <div className="mt-3 border-t border-white/6 pt-3">
                    <div className="flex flex-col gap-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                            <SectionLabel label="Person" className="mb-0 flex-shrink-0 text-[11px] text-[#7E88A8]" />
                            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                                {personSocials.map((s, i) => {
                                    const emailScoreConfig = s.p === 'email' ? getEmailScoreConfig(s.emailScore) : null;
                                    const isEmail = s.p === 'email';
                                    let actionUrl = '#';
                                    if (isEmail) {
                                        actionUrl = `mailto:${s.v}`;
                                    } else if (s.p === 'phone' || s.p === 'mobile') {
                                        actionUrl = `tel:${s.v}`;
                                    } else {
                                        actionUrl = formatSocialURL(s.v, s.p) || '#';
                                    }

                                    return (
                                        <a
                                            key={i}
                                            href={actionUrl}
                                            title={`${s.l}: ${s.v}`}
                                            target={isEmail || s.p === 'phone' || s.p === 'mobile' ? '_self' : '_blank'}
                                            rel="noreferrer"
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-300 transition-all duration-300 hover:bg-white/[0.06] hover:text-white"
                                        >
                                            <SocialIcon
                                                platform={s.p}
                                                className="h-4.5 w-4.5"
                                                style={emailScoreConfig ? { color: emailScoreConfig.color } : undefined}
                                            />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <SectionLabel label="Company" className="mb-0 flex-shrink-0 text-[11px] text-[#7E88A8]" />
                            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                                {companySocials.map((s, i) => {
                                    let actionUrl = '#';
                                    if (s.p === 'email') {
                                        actionUrl = `mailto:${s.v}`;
                                    } else if (s.p === 'phone') {
                                        actionUrl = `tel:${s.v}`;
                                    } else {
                                        actionUrl = formatSocialURL(s.v, s.p, true) || '#';
                                    }

                                    return (
                                        <a
                                            key={i}
                                            href={actionUrl}
                                            title={`${s.l}: ${s.v}`}
                                            target={s.p === 'email' || s.p === 'phone' ? '_self' : '_blank'}
                                            rel="noreferrer"
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-300 transition-all duration-300 hover:bg-white/[0.06] hover:text-white"
                                        >
                                            <SocialIcon platform={s.p} className="h-4.5 w-4.5" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: Company Logo */}
            <div className="flex justify-start xl:justify-end">
                <div className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-[18px] border border-white/10 bg-white/5 shadow-[0_14px_28px_rgba(0,0,0,0.2)]">
                    {data.companyLogo && !companyLogoError ? (
                        <img
                            src={data.companyLogo}
                            alt="Company Logo"
                            className="relative z-10 h-full w-full object-cover transition-transform duration-500"
                            onError={() => setCompanyLogoError(true)}
                        />
                    ) : (
                        <div className="relative z-10 flex h-full w-full flex-col items-center justify-center text-gray-700">
                            {data.companyName ? (
                                <div className="text-2xl font-bold">
                                    {getInitials(data.companyName)}
                                </div>
                            ) : (
                                <svg className="w-12 h-12 text-gray-300 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            )}
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none"></div>
                </div>
            </div>
        </div>
      </div>

      {/* ICP Data Section - NEW LVL2 DATA */}
      {data.personLvl2?.icp && (() => {
        try {
          const icpData = typeof data.personLvl2.icp === 'string' ? JSON.parse(data.personLvl2.icp) : data.personLvl2.icp;
          if (!icpData) return null;
          return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Top Pain */}
            {icpData.top_pain && typeof icpData.top_pain === 'string' && (
              <Card className="bg-gradient-to-br from-red-500/10 to-red-900/5 border-red-500/20 p-3">
                <SectionLabel label="Top Pain" className="text-red-400 text-xs" />
                <p className="text-gray-200 text-xs leading-relaxed mt-1">{icpData.top_pain}</p>
              </Card>
            )}

            {/* Trigger */}
            {icpData.trigger && typeof icpData.trigger === 'string' && (
              <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-900/5 border-yellow-500/20 p-3">
                <SectionLabel label="Trigger" className="text-yellow-400 text-xs" />
                <p className="text-gray-200 text-xs leading-relaxed mt-1">{icpData.trigger}</p>
              </Card>
            )}

            {/* Value Hook */}
            {icpData.value_hook && typeof icpData.value_hook === 'string' && (
              <Card className="bg-gradient-to-br from-green-500/10 to-green-900/5 border-green-500/20 p-3">
                <SectionLabel label="Value Prop" className="text-green-400 text-xs" />
                <p className="text-gray-200 text-xs leading-relaxed mt-1">{icpData.value_hook}</p>
              </Card>
            )}

            {/* Proof */}
            {icpData.proof && typeof icpData.proof === 'string' && (
              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-900/5 border-blue-500/20 p-3">
                <SectionLabel label="Proof" className="text-blue-400 text-xs" />
                <p className="text-gray-200 text-xs leading-relaxed mt-1">{icpData.proof}</p>
              </Card>
            )}
          </div>
          );
        } catch (error) {
          console.error('Error parsing ICP data:', error);
          return null;
        }
      })()}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Executive Summary Card */}
          <Card className="lg:col-span-2 flex flex-col">
              <CardHeader title="Executive Summary">
                  <span className="inline-flex items-center rounded-full border border-white/8 bg-white/[0.04] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8F9BBE]">
                    AI Generated
                  </span>
              </CardHeader>
              <div className="prose prose-invert max-w-none">
                  <p className="text-[13px] leading-6 text-[#C9D1E7]">
                    {data.personLvl2Overview || data.leadExecutiveOverview}
                  </p>
              </div>

              {data.targetAudience && (
                <div className="mt-3 pt-3 border-t border-white/5">
                    <SectionLabel label="Target Audience" />
                    <div className="rounded-[16px] border border-white/8 bg-white/[0.035] p-3">
                        <p className="text-sm italic leading-6 text-[#D6DCEF]">"{data.targetAudience}"</p>
                    </div>
                </div>
              )}
          </Card>

          {/* Right: Direct Contact & Company Metrics */}
          <div className="space-y-3">
              {/* Contact Details Card - MOVED TO FIRST */}
              <Card>
                  <CardHeader title="Direct Contact" />
                  <div className="space-y-2">
                      {contacts.map((c, i) => {
                          // Each row carries its verification pill — email via
                          // provider scoring, other channels via the lvl2
                          // `xx_id_enriched` timestamp (value ⇒ verified).
                          const enrichedAt = data.channelEnrichedAt || {};
                          const channelBadge = c.label === 'Email' ? null
                            : c.label === 'Mobile' ? { channel: 'mobile' as const, at: enrichedAt.mobile }
                            : c.label === 'Phone' ? { channel: 'phone' as const, at: enrichedAt.phone }
                            : c.label === 'Website' ? { channel: 'website' as const, at: enrichedAt.website }
                            : null;
                          return (
                          <div key={i} className="flex items-center gap-2.5 rounded-[16px] border border-white/8 bg-white/[0.03] px-3 py-2.5 group">
                              <svg className="h-3.5 w-3.5 flex-shrink-0 text-gray-600 transition-colors group-hover:text-[#5260FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">{c.icon}</svg>
                              <div className="flex-1 min-w-0">
                                  {c.isLink ? (
                                      <a href={c.href} target="_blank" rel="noreferrer" className="block truncate text-sm text-[#8DD8FF] hover:text-white hover:underline">{c.value}</a>
                                  ) : (
                                      <div className="truncate text-sm text-gray-300">{c.value}</div>
                                  )}
                              </div>
                              {c.label === 'Email' && (
                                  <EmailIntelBadge email={c.value} data={data.emailProviderData} />
                              )}
                              {channelBadge && (
                                  <ChannelVerifiedBadge channel={channelBadge.channel} enrichedAt={channelBadge.at} value={c.value} />
                              )}
                          </div>
                          );
                      })}
                  </div>
              </Card>

              {/* Company Metrics Card - MOVED TO SECOND */}
              <Card>
                  <CardHeader title="Company Metrics" />
                  <div className="space-y-2">
                      {keyMetrics.map((m, i) => (
                          <div key={i} className="flex items-center justify-between rounded-[16px] border border-white/8 bg-white/[0.03] px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                  <div className={`rounded-lg border border-white/8 bg-white/[0.03] p-2 ${m.color}`}>
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">{m.icon}</svg>
                                  </div>
                                  <span className="text-sm text-gray-400">{m.label}</span>
                              </div>
                              <span className="max-w-[132px] truncate text-right text-sm font-semibold text-white">{m.value}</span>
                          </div>
                      ))}
                  </div>
              </Card>
          </div>
      </div>

      {/* Bottom Section: Location, VAT & Differentiation */}
      <div className="grid grid-cols-3 gap-3">
          <Card className="bg-gradient-to-br from-[#121212] to-[#0a0a0a] hover:border-[#5260FF]/20 relative overflow-hidden group p-3">
              <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                   <svg className="w-12 h-12 text-[#5260FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
              <SectionLabel label="Location" className="text-xs" />
              <div className="space-y-1 relative z-10">
                  <div className="text-base font-bold text-white">{data.companyCountry || 'Unknown'}</div>
                  <div className="text-xs text-gray-400 line-clamp-1">
                    {[
                      data.postalCode && data.postalCode !== 'Not specified' ? data.postalCode : null,
                      data.companyCity && data.companyCity !== 'Not specified' ? data.companyCity : null,
                      data.neighborhood && data.neighborhood !== 'Not specified' ? data.neighborhood : null,
                      data.companyState && data.companyState !== 'Not specified' ? data.companyState : null,
                    ].filter(Boolean).join(', ')}
                  </div>
              </div>
          </Card>

          {data.companyVat && (
            <Card className="bg-gradient-to-br from-[#121212] to-[#0a0a0a] hover:border-[#4FCDCF]/20 relative overflow-hidden group p-3">
                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                     <svg className="w-12 h-12 text-[#4FCDCF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                </div>
                <SectionLabel label="VAT / Tax ID" className="text-xs" />
                <div className="space-y-1 relative z-10">
                    <div className="text-sm font-bold text-white font-mono">{data.companyVat}</div>
                </div>
            </Card>
          )}

          <Card className="bg-gradient-to-br from-[#121212] to-[#0a0a0a] hover:border-[#15FBFF]/20 relative overflow-hidden group p-3">
              <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                   <svg className="w-12 h-12 text-[#15FBFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              </div>
              <SectionLabel label="Key Differentiators" className="text-xs" />
              <div className="space-y-1 relative z-10">
                  {data.differentiationFactors && data.differentiationFactors.length > 0 ? (
                      data.differentiationFactors.slice(0, 2).map((factor: string, i: number) => (
                          <div key={i} className="flex items-start gap-2">
                              <div className="w-1 h-1 rounded-full bg-[#15FBFF] mt-1.5 flex-shrink-0"></div>
                              <p className="text-xs text-gray-300 leading-snug line-clamp-1">{factor}</p>
                          </div>
                      ))
                  ) : (
                      <div className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-gray-700 mt-1.5"></div>
                          <p className="text-xs text-gray-600 italic">No data</p>
                      </div>
                  )}
              </div>
          </Card>
      </div>
    </div>
  );
};
